import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';

import { pb, getCurrentUserId } from '@/core/api';

import type {
  XFollowedAccount,
  XFollowedAccountInput,
} from '@/types/pocketbase';

const QUERY_KEY = ['followed-accounts'] as const;

export function useFollowedAccounts(): UseQueryResult<XFollowedAccount[]> {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<XFollowedAccount[]> => {
      const response = await pb
        .collection('x_followed_accounts')
        .getFullList<XFollowedAccount>({ sort: '-created' });
      return response;
    },
  });
}

type CreateResult = UseMutationResult<
  XFollowedAccount,
  Error,
  XFollowedAccountInput
>;

export function useCreateFollowedAccount(): CreateResult {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: XFollowedAccountInput) => {
      return pb
        .collection('x_followed_accounts')
        .create<XFollowedAccount>({ ...data, user: getCurrentUserId() });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

type UpdateInput = { id: string; data: Partial<XFollowedAccountInput> };
type UpdateResult = UseMutationResult<XFollowedAccount, Error, UpdateInput>;

export function useUpdateFollowedAccount(): UpdateResult {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: UpdateInput) => {
      return pb
        .collection('x_followed_accounts')
        .update<XFollowedAccount>(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

type DeleteResult = UseMutationResult<boolean, Error, string>;

export function useDeleteFollowedAccount(): DeleteResult {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return pb.collection('x_followed_accounts').delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
